/**
 * Aggregates every `<entity>.events.js` file plus the lifecycle-only catalog
 * into a single in-memory `EVENT_CATALOG` keyed by canonical event name.
 *
 * Adding a new entity = create `<entity>.events.js` and import it here. (Or
 * append to `_lifecycle-entities.js` for plain CRUD entities.) No other code
 * changes are required (OCP).
 */

import auth from './auth.events.js';
import user from './user.events.js';
import role from './role.events.js';
import delegation from './delegation.events.js';
import risk from './risk.events.js';
import riskRegister from './riskRegister.events.js';
import incident from './incident.events.js';
import problem from './problem.events.js';
import asset from './asset.events.js';
import assetLicense from './assetLicense.events.js';
import assetDisposal from './assetDisposal.events.js';
import vendor from './vendor.events.js';
import vendorAssessment from './vendorAssessment.events.js';
import document from './document.events.js';
import control from './control.events.js';
import ticket from './ticket.events.js';
import bcp from './bcp.events.js';
import bia from './bia.events.js';
import crisisEvent from './crisisEvent.events.js';
import exercise from './exercise.events.js';
import training from './training.events.js';
import assessment from './assessment.events.js';
import audit from './audit.events.js';
import gdpr from './gdpr.events.js';
import dataBreach from './dataBreach.events.js';
import rootCause from './rootCause.events.js';
import workflow from './workflow.events.js';
import automation from './automation.events.js';
import party from './party.events.js';
import tenant from './tenant.events.js';
import permission from './permission.events.js';
import unit from './unit.events.js';
import integrationConnection from './integrationConnection.events.js';
import privacyNotice from './privacyNotice.events.js';
import dpiaAssessment from './dpiaAssessment.events.js';
import disposalRun from './disposalRun.events.js';
import dataProcessingAgreement from './dataProcessingAgreement.events.js';
import consentRecord from './consentRecord.events.js';
import cookieInventory from './cookieInventory.events.js';
import verbis from './verbis.events.js';
import dataSubjectRequest from './dataSubjectRequest.events.js';
import internationalTransfer from './internationalTransfer.events.js';
import formTask from './formTask.events.js';
import collectionAssignment from './collectionAssignment.events.js';
import collectionCampaign from './collectionCampaign.events.js';
import team from './team.events.js';
import operationalRun from './operationalRun.events.js';
import assetGroupCriticalityEvaluation from './assetGroupCriticalityEvaluation.events.js';
import bigdesCurrentSituationEvaluationScope from './bigdesCurrentSituationEvaluationScope.events.js';
import bigdesImplementationProcessEvaluation from './bigdesImplementationProcessEvaluation.events.js';
import objective from './objective.events.js';
import { LIFECYCLE_ONLY_CATALOG } from './_lifecycle-entities.js';

const dedicated = [
    auth, user, role, delegation,
    risk, riskRegister, incident, problem, asset, assetLicense, assetDisposal, vendor, vendorAssessment, document, control, ticket,
    bcp, bia, crisisEvent, exercise,
    training, assessment, audit, gdpr, dataBreach, rootCause,
    workflow, automation, party, tenant, permission, unit,
    integrationConnection,
    privacyNotice, dpiaAssessment, disposalRun, dataProcessingAgreement, consentRecord, cookieInventory, verbis,
    dataSubjectRequest, internationalTransfer, formTask, collectionAssignment, collectionCampaign, team, operationalRun,
    assetGroupCriticalityEvaluation, bigdesCurrentSituationEvaluationScope, bigdesImplementationProcessEvaluation,
    objective,
];

/**
 * EVENT_CATALOG: { [canonicalEventName]: EventDef }
 * ENTITY_CATALOG: { [entity]: { entity, events: { [verbKey]: EventDef } } }
 *
 * Dedicated entity files take precedence over lifecycle-only entries when both
 * register the same entity (e.g. `risk` exists in both). Within an entity,
 * dedicated definitions cannot collide because catalog keys are unique.
 */
export const ENTITY_CATALOG = Object.create(null);
export const EVENT_CATALOG = Object.create(null);

function registerEntity(entry) {
    const existing = ENTITY_CATALOG[entry.entity];
    if (existing) {
        // Merge events (dedicated entries override lifecycle defaults)
        const merged = { entity: entry.entity, events: { ...existing.events, ...entry.events } };
        ENTITY_CATALOG[entry.entity] = merged;
    } else {
        ENTITY_CATALOG[entry.entity] = entry;
    }
    for (const def of Object.values(entry.events)) {
        if (!def?.name) continue;
        EVENT_CATALOG[def.name] = def;
    }
}

// Lifecycle-only entries first so dedicated files override them on conflict.
for (const entry of LIFECYCLE_ONLY_CATALOG) registerEntity(entry);
for (const entry of dedicated) registerEntity(entry);
